
import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import CookieAnalytics from '../components/admin/CookieAnalytics';
import PolicyAcceptanceRecords from '../components/admin/PolicyAcceptanceRecords';
import EmployerManagement from '../components/admin/EmployerManagement';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { toast } from 'sonner';
import { getDeploymentInfo } from '../utils/routingUtils';

const AdminPanel = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { tab } = useParams<{ tab?: string }>();
  const [activeTab, setActiveTab] = useState('analytics');
  const [password, setPassword] = useState('');
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [deploymentInfo, setDeploymentInfo] = useState({ platform: '', environment: '', deployType: '' });
  
  // In a real application, this would be stored securely
  // For demo purposes, we're using a simple password
  const ADMIN_PASSWORD = 'admin123';
  
  useEffect(() => {
    console.log('Admin Panel mounting, getting deployment info');
    const info = getDeploymentInfo();
    setDeploymentInfo(info);
    
    console.log('Admin Panel loaded at:', window.location.href);
    console.log('Current URL path:', location.pathname);
    console.log('Tab parameter:', tab);
    
    // Check if authenticated in session storage
    const storedAuth = sessionStorage.getItem('adminAuthenticated');
    if (storedAuth === 'true') {
      setIsAuthenticated(true);
      console.log('Admin already authenticated via session storage');
    }
    
    // Set active tab based on URL parameter
    if (tab) {
      if (['analytics', 'consents', 'settings'].includes(tab)) {
        setActiveTab(tab);
        console.log('Setting active tab to:', tab);
      } else {
        // If invalid tab parameter, redirect to default
        navigate('/admin', { replace: true });
      }
    }
  }, [tab, navigate, location.pathname]);

  // Handle login attempt
  const handleLogin = (e: React.FormEvent) => {
    e.preventDefault();
    console.log('Login attempt');
    
    if (password === ADMIN_PASSWORD) {
      console.log('Admin login successful');
      setIsAuthenticated(true);
      sessionStorage.setItem('adminAuthenticated', 'true');
      setLoginError('');
      toast.success('Successfully logged in to admin panel');
    } else {
      console.log('Admin login failed');
      setLoginError('Invalid password. Please try again.');
      toast.error('Invalid admin password');
    }
    
    // Clear password field
    setPassword('');
  };

  // Handle logout
  const handleLogout = () => {
    console.log('Admin logout');
    setIsAuthenticated(false);
    sessionStorage.removeItem('adminAuthenticated');
    toast.info('Logged out from admin panel');
  };

  // Update URL when tab changes
  const handleTabChange = (value: string) => {
    console.log('Tab changed to:', value);
    setActiveTab(value);
    
    // Update the URL to match the selected tab
    if (value === 'analytics') {
      navigate('/admin', { replace: true });
    } else {
      navigate(`/admin/${value}`, { replace: true });
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="container mx-auto py-12 px-4 max-w-md">
        <Card>
          <CardHeader>
            <CardTitle>Admin Login</CardTitle>
            <CardDescription>
              Please enter the admin password to continue
            </CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleLogin} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input 
                  id="password" 
                  type="password" 
                  value={password} 
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="Enter admin password"
                  required
                />
              </div>
              {loginError && (
                <p className="text-sm text-red-500">{loginError}</p>
              )}
              <Button type="submit" className="w-full">
                Login
              </Button>
            </form>
            <div className="mt-4 p-2 bg-gray-100 rounded text-xs">
              <p>Debug Info (Admin Panel Login):</p>
              <p>Path: {location.pathname}</p>
              <p>Hostname: {window.location.hostname}</p>
              <p>Platform: {deploymentInfo.platform}</p>
              <p>Environment: {deploymentInfo.environment}</p>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-12 px-4">
      <div className="flex justify-between items-center mb-8">
        <h1 className="text-3xl font-bold">Admin Panel</h1>
        <Button variant="outline" onClick={handleLogout}>
          Logout
        </Button>
      </div>
      
      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList className="grid w-full grid-cols-3 mb-8">
          <TabsTrigger value="analytics">Analytics</TabsTrigger>
          <TabsTrigger value="consents">User Consents</TabsTrigger>
          <TabsTrigger value="settings">Settings</TabsTrigger>
        </TabsList>
        
        <TabsContent value="analytics">
          <Card>
            <CardHeader>
              <CardTitle>Cookie Analytics</CardTitle>
              <CardDescription>
                View analytics about cookie acceptance on your platform
              </CardDescription>
            </CardHeader>
            <CardContent>
              <CookieAnalytics />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="consents">
          <Card>
            <CardHeader>
              <CardTitle>Policy Acceptance Records</CardTitle>
              <CardDescription>
                Review policy acceptances from users and track consent
              </CardDescription>
            </CardHeader>
            <CardContent>
              <PolicyAcceptanceRecords />
            </CardContent>
          </Card>
        </TabsContent>
        
        <TabsContent value="settings">
          <Card>
            <CardHeader>
              <CardTitle>Admin Settings</CardTitle>
              <CardDescription>
                Manage system settings and configurations
              </CardDescription>
            </CardHeader>
            <CardContent>
              <EmployerManagement />
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
      
      <div className="mt-8 p-4 bg-gray-100 rounded text-xs">
        <h3 className="font-bold mb-2">Debug Information (Admin Panel)</h3>
        <p>Current Path: {location.pathname}</p>
        <p>Hostname: {window.location.hostname}</p>
        <p>Origin: {window.location.origin}</p>
        <p>Platform: {deploymentInfo.platform}</p>
        <p>Environment: {deploymentInfo.environment}</p>
        <p>Tab Parameter: {tab || 'none'}</p>
        <p>Active Tab: {activeTab}</p>
      </div>
    </div>
  );
};

export default AdminPanel;
